import { rgba, shade } from 'polished'
import { keyframes } from '@emotion/react'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'

import bullet from '~media/global/bullet.svg'
import { StylesHeaderVariables } from '~partials/header/UI'
import slantBackgroundMedia from '~media/global/slant-background.svg'

export const getPaddedStickyMenuOffset = (theme) =>
  StylesHeaderVariables(theme).height + theme.space.base

// Attaches sticky properties to element that also takes into account our navigation
export const stickyIt = (theme) => ({
  position: 'sticky',
  top: getPaddedStickyMenuOffset(theme),
})

export const slantBackground = (theme) => ({
  background: `url(${slantBackgroundMedia}) no-repeat center center, linear-gradient(to bottom right, ${theme.brand.darkBlue}, ${theme.brand.purple}), ${theme.brand.darkestBlue}`,
  backgroundSize: 'cover',
  backgroundBlendMode: 'hard-light',
})

export const fadeInUp = (theme) =>
  keyframes({
    from: {
      transform: `translateY(${theme.space.base}px)`,
      opacity: 0,
    },

    to: {
      transform: 'translateY(0px)',
      opacity: 1,
    },
  })

export const fadeIn = () =>
  keyframes({
    from: {
      opacity: 0,
    },

    to: {
      opacity: 1,
    },
  })

export const listBullet = (theme) => {
  const bulletSize = theme.fontSize.xlarge

  return {
    li: {
      position: 'relative',
      paddingLeft: bulletSize + theme.space.base,

      '&::before': {
        content: "''",
        background: `url(${bullet}) no-repeat center center`,
        backgroundSize: 'contain',
        width: bulletSize,
        height: bulletSize,
        position: 'absolute',
        left: 0,
      },
    },
  }
}

// Adjusts article content code blocks on smaller screens
export const gatsbyCodeCollapse = (theme) => ({
  [theme.mq.small__max]: {
    '.gatsby-highlight': {
      marginLeft: -theme.space.base,
      marginRight: -theme.space.base,

      '> pre': {
        borderRadius: 0,
      },
    },
  },
})

// @NOTE Can only be used on inline elements
export const textGradientUnderline = (theme, opts) => {
  const props = {
    ...{
      size: 'large',
      start: theme.brand.teal,
      stop: theme.color.primary.base,
    },
    ...opts,
  }

  return {
    backgroundImage: `linear-gradient(to right, ${props.start}, ${props.stop})`,
    backgroundPosition: '0 100%',
    backgroundSize: `100% ${props.size === 'large' ? '4px' : '2px'}`,
    backgroundRepeat: 'no-repeat',

    [theme.mq.medium]: {
      backgroundSize: `100% ${props.size === 'large' ? '6px' : '3px'}`,
    },
  }
}

export const textGradientBlockUnder = (theme, opts) => {
  const props = {
    ...{
      start: theme.brand.teal,
      stop: theme.color.primary.base,
      align: 'left',
    },
    ...opts,
  }

  return {
    position: 'relative',

    '&::after': [
      {
        content: "''",
        display: 'block',
        marginTop: theme.space.large,
        marginBottom: theme.space.large,
        backgroundImage: `linear-gradient(to right, ${props.start}, ${props.stop})`,
        width: 100,
        height: 4,
      },

      props.align === 'center' && {
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    ],
  }
}

export const textGradientBlockAbove = (theme, opts) => {
  const props = {
    ...{
      start: theme.brand.teal,
      stop: theme.color.primary.base,
    },
    ...opts,
  }

  return {
    position: 'relative',

    '&::before': {
      content: "''",
      display: 'block',
      marginTop: theme.space.large,
      marginBottom: theme.space.large,
      backgroundImage: `linear-gradient(to right, ${props.start}, ${props.stop})`,
      width: 100,
      height: 4,
    },
  }
}

export const sectionSeparator = (theme, props) => ({
  position: 'relative',

  '&::before': {
    content: "''",
    width: 2,
    height: theme.height.base,
    background: generateGradient({
      start: props.start,
      stop: props.stop,
      position: 'to bottom',
    }),
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translate(-50%, -50%)',
  },
})

export const shadowEmboss = (theme, opts) => {
  const props = {
    ...{
      background: shade(0.075, theme.color.light.base),
      size: '10px',
      direction: 'right',
    },
    ...opts,
  }

  const math = props.direction === 'left' ? '-' : ''

  return {
    boxShadow: `
    ${math}1px 0 ${rgba(theme.color.light.base, 0.15)}, ${math}1px 0 ${
      props.background
    },
    ${math}3px 0 0 ${props.background}, ${math}4px 0 0 ${
      props.background
    }, ${math}5px 0 0 ${props.background},
    ${math}6px 0 0 ${props.background}, ${math}7px 0 0 ${
      props.background
    }, ${math}8px 0 0 ${props.background},
    ${math}9px 0 0 ${props.background}, ${math}10px 0 0 ${
      props.background
    }, ${math}11px 0 0 ${props.background},
    ${math}12px 0 0 ${props.background}, ${math}13px 0 0 ${
      props.background
    }, ${math}14px 0 0 ${props.background},
    ${math}15px 0 0 ${props.background}, 0 0 ${props.size} ${rgba(
      theme.color.dark.base,
      0.125
    )}
    `,
  }
}

export const micrositeSectionTitle = (theme, opts) => {
  const props = {
    ...{
      align: 'left',
      border: true,
      size: 'base',
    },
    ...opts,
  }

  return [
    props.size === 'large' && { ...theme.text['3xlarge__fluid'] },

    {
      marginBottom:
        props.size === 'large' ? theme.space.large : theme.space.base,

      [theme.mq.medium]: {
        marginBottom:
          props.size === 'large' ? theme.space.xlarge : theme.space.large,
      },
    },

    props.border &&
      textGradientBlockAbove(theme, {
        start: theme.color.border.base,
        stop: theme.color.border.base,
      }),

    props.align === 'center' && {
      textAlign: 'center',

      '&::before': {
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
  ]
}
